import { useEffect, useMemo, useState } from 'react';
import {
  useCreate,
  useGetLocale,
  useList,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Alert, Col, Divider, Form, Input, Modal, Row, Select } from 'antd';
import type { ContentPublisherOut } from '@/api/generated/schemas';
import { extractBackendMessage } from '@/utils/apiError';
import { countryOptions } from '@/utils/countries';
import {
  toPublisherCreatePayload,
  type PublisherCreateValues,
} from './publisherPayload';

const NAME_CHECK_DELAY_MS = 400;

interface PublisherCreateModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the created publisher once the modal has closed. */
  onCreated: (publisher: ContentPublisherOut) => void;
}

export const PublisherCreateModal = ({
  open,
  onClose,
  onCreated,
}: PublisherCreateModalProps) => {
  const t = useTranslate();
  const locale = useGetLocale()() ?? 'pl';
  const { open: notify } = useNotification();
  const { mutateAsync } = useCreate<ContentPublisherOut>();
  const [form] = Form.useForm<PublisherCreateValues>();
  const [submitting, setSubmitting] = useState(false);

  const tk = (suffix: string) =>
    t(`content_catalog.publishers.create.${suffix}`);
  const tf = (field: string) => t(`content_catalog.publishers.fields.${field}`);

  const countries = useMemo(() => countryOptions(locale), [locale]);

  // Publishers may share a name, so an existing one only warns.
  const name = Form.useWatch('name', form);
  const [checkedName, setCheckedName] = useState('');
  useEffect(() => {
    const timer = setTimeout(
      () => setCheckedName(name?.trim() ?? ''),
      NAME_CHECK_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [name]);
  const { result: sameName } = useList<ContentPublisherOut>({
    resource: 'content_catalog.publishers',
    filters: [{ field: 'name', operator: 'eq', value: checkedName }],
    pagination: { pageSize: 1, mode: 'server' },
    queryOptions: { enabled: open && checkedName !== '' },
  });
  const nameTaken = checkedName !== '' && (sameName?.total ?? 0) > 0;

  useEffect(() => {
    if (open) {
      form.resetFields();
      setCheckedName('');
    } else {
      setSubmitting(false);
    }
  }, [open, form]);

  const handleSubmit = async () => {
    let values: PublisherCreateValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await mutateAsync({
        resource: 'content_catalog.publishers',
        values: toPublisherCreatePayload(values),
        successNotification: false,
        errorNotification: false,
      });
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk('success'),
      });
      onClose();
      onCreated(data);
    } catch (error) {
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description: extractBackendMessage(error) ?? tk('error_fallback'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const maxLength = (max: number) => ({
    max,
    message: t('content_catalog.publishers.create.errors.max_length', { max }),
  });

  return (
    <Modal
      open={open}
      title={tk('title')}
      okText={tk('submit')}
      okButtonProps={{ loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
      width={640}
    >
      <Alert
        type="info"
        showIcon
        message={tk('hints.immutable')}
        style={{ marginBottom: 16 }}
      />
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
        initialValues={{ country_code: 'PL' }}
      >
        <Form.Item
          name="name"
          label={tf('name')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: tk('errors.name_required'),
            },
            maxLength(512),
          ]}
          validateStatus={nameTaken ? 'warning' : undefined}
          help={nameTaken ? tk('hints.name_taken') : undefined}
        >
          <Input autoFocus />
        </Form.Item>
        <Form.Item
          name="legal_name"
          label={tf('legal_name')}
          rules={[maxLength(512)]}
        >
          <Input />
        </Form.Item>

        <Divider orientation="left" plain>
          {t('content_catalog.publishers.sections.contact')}
        </Divider>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label={tf('email')}
              rules={[
                { type: 'email', message: tk('errors.email_invalid') },
                maxLength(255),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="telephone"
              label={tf('telephone')}
              rules={[maxLength(100)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="street_address"
          label={tf('street_address')}
          rules={[maxLength(512)]}
        >
          <Input />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="postal_code"
              label={tf('postal_code')}
              rules={[maxLength(20)]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item
              name="location_name"
              label={tf('location_name')}
              rules={[maxLength(255)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="country_code" label={tf('country')}>
          <Select
            showSearch
            allowClear
            options={countries}
            optionFilterProp="label"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
